import { BareActionContext, getStoreBuilder } from 'vuex-typex';
import axios from 'axios';
import { RootState } from '@/store/state';
import { ProductsState } from './state';
import appStore from '@/store/app';
import { Api, ApiResult, ProductDetail, LogisticCost, WholesalerInfo, ProductAvailability, SingleResultOf, TypeOfSearch, ResultOf, Product } from '@/types';
import { ProductsSearch } from '@/models';

type StoreContext = BareActionContext<ProductsState, RootState>;

const thisState: ProductsState = {
   products: [],
   totalProducts: 0,
   search: new ProductsSearch(),
   product: undefined,
   availability: undefined,
};

const createEmptyProduct: () => ProductDetail = () => {
   return {
      id: 0,
      logisticCosts: {
         margin: 0,
         markUp: 0,
         supplierPrice: 0,
         lineTotal: 0,
         amount: 0,
      } as LogisticCost,
      wholesalerInfo: {
         fan: '',
         weight: 0,
         volume: 0,
         isStock: false,
      } as WholesalerInfo,
   } as ProductDetail;
};

const createEmptyAvailability: () => ProductAvailability = () => {
   return { specialOfferCode: '', quantityAvailable: '', notes: '...' } as ProductAvailability;
};

const moduleBuilder = getStoreBuilder<RootState>().module('products', thisState);

const thisModule = {
   getters: {
      products(state: ProductsState): Product[] {
         return state.products;
      },

      totalProducts(state: ProductsState): number {
         return state.totalProducts;
      },

      product(state: ProductsState): ProductDetail {
         return state.product || createEmptyProduct();
      },

      availability(state: ProductsState): ProductAvailability {
         return state.availability || createEmptyAvailability();
      },

      search(state: ProductsState): ProductsSearch {
         return state.search;
      },
   },

   actions: {

      async fetchProductsAsync(context: StoreContext, payload: ProductsSearch): Promise<void> {

         if (!payload) { return; }

         appStore.loading({ isLoading: true });

         const keywordSearch = payload.keyword === '' ? '*' : payload.keyword;

         return await axios.get<ResultOf<Product>>(Api.BaseUrl + `products/search/${keywordSearch}/${payload.searchType}/${payload.searchInHistorical}`, {
            params: {
               page: payload.pageNumber,
               size: payload.pageSize,
               sortField: payload.sortField,
               sortDirection: payload.sortDirection,
            },
         })
            .then((response) => {
               commitPopulateProducts({ products: response.data.payload, total: response.data.total, search: payload });
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateProducts({ products: [], total: 0, search: payload });
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async fetchProductAsync(context: StoreContext, payload: { type: string, pharmacode: string, quantity: number }): Promise<void> {

         if (!payload.type || !payload.pharmacode) {
            commitPopulateProduct(createEmptyProduct());
            return;
         }

         appStore.loading({ isLoading: true });

         return await axios.get<SingleResultOf<ProductDetail>>(Api.BaseUrl + `products/${payload.type}/${payload.pharmacode}`, {
            params: {
               quantity: payload.quantity,
            },
         })
            .then((response) => {
               const product = response.data.payload || createEmptyProduct();
               commitPopulateProduct(product);
               if (product.warnings) {
                  appStore.warnings({ messages: product.warnings, timeout: 9000 });
               }
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
               }
               commitPopulateProduct(createEmptyProduct());
            })
            .finally(() => {
               appStore.loading({ isLoading: false });
            });
      },

      async fetchProductAvailabilityAsync(context: StoreContext, payload: { type: string, pharmacode: string }): Promise<void> {

         if (!payload.type || !payload.pharmacode) {
            return;
         }

         return await axios.get<SingleResultOf<ProductAvailability>>(Api.BaseUrl + `products/availability/${payload.type}/${payload.pharmacode}`)
            .then((response) => {
               commitPopulateAvailability(response.data.payload || createEmptyAvailability());
            })
            .catch((reason) => {
               if (reason && reason.response) {
                  if (reason.response.status === 404) {
                     appStore.warning({ message: 'Impossibile reperire informazioni sulla disponibilità dell\'articolo.' });
                  } else {
                     appStore.errors({ status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true });
                  }
               }
            });
      },
   },

   mutations: {

      populateProducts(state: ProductsState, payload: { products: Product[], total: number, search: ProductsSearch }) {
         state.products = payload.products;
         state.totalProducts = payload.total;
         state.search = payload.search;
      },

      populateProduct(state: ProductsState, payload: ProductDetail) {
         state.product = payload;
      },

      populateAvailability(state: ProductsState, payload: ProductAvailability) {
         state.availability = payload;
      },
   },
};

// private getters
const getProducts = moduleBuilder.read(thisModule.getters.products, 'getProducts');
const getTotalProducts = moduleBuilder.read(thisModule.getters.totalProducts, 'getTotalProducts');
const getProduct = moduleBuilder.read(thisModule.getters.product, 'getProduct');
const getAvailability = moduleBuilder.read(thisModule.getters.availability, 'getAvailability');
const getSearch = moduleBuilder.read(thisModule.getters.search, 'getSearch');

// mutations
const commitPopulateProducts = moduleBuilder.commit(thisModule.mutations.populateProducts, 'commitPopulateProducts');
const commitPopulateProduct = moduleBuilder.commit(thisModule.mutations.populateProduct, 'commitPopulateProduct');
const commitPopulateAvailability = moduleBuilder.commit(thisModule.mutations.populateAvailability, 'commitPopulateAvailability');

const products = {

   // getters (wrapped as real getters)
   get products() { return getProducts(); },
   get totalProducts() { return getTotalProducts(); },
   get product() { return getProduct(); },
   get availability() { return getAvailability(); },
   get search() { return getSearch(); },

   // actions
   fetchProductsAsync: moduleBuilder.dispatch(thisModule.actions.fetchProductsAsync, 'fetchProductsAsync'),
   fetchProductAsync: moduleBuilder.dispatch(thisModule.actions.fetchProductAsync, 'fetchProductAsync'),
   fetchProductAvailabilityAsync: moduleBuilder.dispatch(thisModule.actions.fetchProductAvailabilityAsync, 'fetchProductAvailabilityAsync'),
};

export default products;
